
import { Component, Input, OnChanges, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormArray, FormControl } from "@angular/forms";
import { isEmpty, some } from "lodash";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { AllocationType } from "rl-common/services/accounting-processes/models/accounting-process-allocation-type";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { AllocationService } from "rl-common/services/allocation/allocation.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";
import { AccountOperationEventComponent } from "../account-operation-event/account-operation-event.component";
import { AddAccountingOperationEventComponent } from "../add-accounting-operation-event/add-accounting-operation-event.component";
import { IBuildAccountingOperationEvent } from "../build-accounting-operation/models/i-build-accounting-operation-event";
import { IBuildAccountingOperationTrigger } from "../build-accounting-operation/models/i-build-accounting-operation-trigger";

@Component({
	selector: "rl-add-accounting-operation-events",
	templateUrl: "./add-accounting-operation-events.component.html",
	styleUrls: ["./add-accounting-operation-events.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AddAccountingOperationEventComponent, AccountOperationEventComponent]
})
export class AddAccountingOperationEventsComponent implements OnInit, OnChanges, OnDestroy {
	private readonly _promptService = inject(PromptService);
	private readonly _allocationService = inject(AllocationService);


	@Input()
	anchorTemplate: ICharacteristicTemplate;

	@Input()
	eventsFormArray: FormArray<FormControl<IBuildAccountingOperationEvent>>;

	@Input()
	disabled = false;

	@Input()
	accounts: IAccountingProcessAccount[] = [];

	@Input()
	accountGroups: IAccountingProcessAccountGroup[] = [];

	@Input()
	workflowTrigger: IBuildAccountingOperationTrigger;

	@Input()
	operationType: number = null;

	@Input()
	costAnchorTemplateId: number;

	editingIndex: number = null;
	revenueAllocationTemplateId: number;
	costAllocationTemplateId: number;
	hasCostAllocation = false;
	hasCostAllocationTemplate = false;

	get eventControls() {
		return this.eventsFormArray.controls;
	}

	get canAddAnother() {
		if (this.editingIndex != null) {
			return false;
		}
		const controls = this.eventControls;
		if (isEmpty(controls)) {
			return true;
		}
		const hasSavedEvents = some(controls, control => !!control.value.id);
		return hasSavedEvents;
	}

	private readonly _subscriptions: Subscription[] = [];

	ngOnInit(): void {
		if (this.eventsFormArray.length == 0) {
			this.addEvent(false);
		} else if (this.editingIndex === null) {
			// If any existing events are missing revenue accounts (e.g. copied process),
			// auto-open the first one so the user is guided through configuration.
			const firstEventMissingRev = this.eventControls.findIndex(control => {
				const event = control.value;
				return !event.accountOrGroups?.some(x => x.allocationType === AllocationType.Revenue && x.debitId?.id != null && x.creditId?.id != null);
			});
			if (firstEventMissingRev >= 0) {
				this.editingIndex = firstEventMissingRev;
			}
		}
		const sub = this._allocationService.getAllocationTemplateConfiguration().pipe(
			tap(result => {
				this.revenueAllocationTemplateId = result.revenueAllocationTemplateId;
				this.costAllocationTemplateId = result.costAllocationTemplateId;
				this.hasCostAllocation = result.costAllocationTemplateId > 0;
			})
		).subscribe();
		this._subscriptions.push(sub);
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.eventsFormArray && changes.eventsFormArray.currentValue?.controls?.length == 0 && changes.eventsFormArray.previousValue?.controls?.length > 0) {
			this.addEvent(false);
		}
		if (changes.costAnchorTemplateId) {
			const previousHadTemplate = !!changes.costAnchorTemplateId.previousValue;
			const currentHasTemplate = !!this.costAnchorTemplateId;
			this.hasCostAllocationTemplate = currentHasTemplate;

			// If cost template was added (went from None to a template), force all events into edit mode
			if (!previousHadTemplate && currentHasTemplate && this.eventControls.length > 0) {
				// Check if any event is missing cost allocation accounts
				const firstEventMissingCost = this.eventControls.findIndex(control => {
					const event = control.value;
					const hasCostAccounts = event.accountOrGroups?.some(x => x.allocationType === AllocationType.Cost && x.debitId?.id != null && x.creditId?.id != null);
					return !hasCostAccounts;
				});

				// Edit the first event that needs cost accounts
				if (firstEventMissingCost >= 0) {
					this.editingIndex = firstEventMissingCost;
				}
			}
		}
	}

	editEvent(index: number) {
		this.editingIndex = index;
	}

	cancelEdit(index: number) {
		const control = this.eventControls[index];
		if (!control.value.id) {
			this.eventsFormArray.removeAt(index, { emitEvent: false });
		}
		this.editingIndex = null;
	}

	updateEvent(event: IBuildAccountingOperationEvent, index: number) {
		this.eventControls[index].setValue(event);
		this.editingIndex = null;

		// Auto-progress to the next event that still needs accounts configured.
		// Prefer any event missing revenue accounts (backend requires them for every event),
		// then any event missing cost accounts when a cost template is selected.
		const nextEventMissingRev = this.eventControls.findIndex((control, i) => {
			if (i <= index) return false;
			const evt = control.value;
			const hasRevAccounts = evt.accountOrGroups?.some(x => x.allocationType === AllocationType.Revenue && x.debitId?.id != null && x.creditId?.id != null);
			return !hasRevAccounts;
		});
		if (nextEventMissingRev >= 0) {
			this.editingIndex = nextEventMissingRev;
			return;
		}

		if (this.hasCostAllocationTemplate) {
			const nextEventMissingCost = this.eventControls.findIndex((control, i) => {
				if (i <= index) return false;
				const evt = control.value;
				const hasCostAccounts = evt.accountOrGroups?.some(x => x.allocationType === AllocationType.Cost && x.debitId?.id != null && x.creditId?.id != null);
				return !hasCostAccounts;
			});
			if (nextEventMissingCost >= 0) {
				this.editingIndex = nextEventMissingCost;
			}
		}
	}

	addEvent(emitEvent: boolean = true) {
		const control = new FormControl<IBuildAccountingOperationEvent>(({} as IBuildAccountingOperationEvent));
		const nextIndex = this.eventsFormArray.length;
		this.eventsFormArray.insert(nextIndex, control, { emitEvent });
		this.editEvent(nextIndex);
	}

	removeEvent(index: number, confirmDeleteDialog: TemplateRef<unknown>) {
		const sub = this._promptService.confirmMini("Confirmation", confirmDeleteDialog,).pipe(
			filter(confirm => confirm),
		).subscribe(() => {
			this.eventsFormArray.removeAt(index);
		});

		this._subscriptions.push(sub);
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach(sub => sub.unsubscribe());
	}

}
